import { createContext, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { displayError, displayErrorExpired, displaySessionRenewed, displaySuccess } from '../utils/messageToast';

import { LoggedInUserProps, AuthContextData, SignInProps, AuthProviderProps } from '../utils/functionsInterfaces';

import { SetupNotAuth } from '../client';

import {

  resetUnauthorizedHandledFlag,
  setRefreshBeforeUnauthorizedHandler,
  setSessionUnauthorizedHandler,
} from '../auth/unauthorizedSession';

import {

  getDefaultSessionMs,

  getRemainingSessionMs,

  isSessionExpired,

  writeLogoutExpiry,

} from '../auth/sessionExpiry';

import { useSessionExpiryGuard } from '../hooks/useSessionExpiryGuard';
import { AUTH_KEYS } from '../auth/authKeys';
import { silentRefreshSession } from '../auth/sessionRenewal';

export { AUTH_KEYS };



/** @deprecated Importe de `../auth/sessionExpiry` */

export { parseLogoutExpiryFromStorage, type ParsedLogoutExpiry } from '../auth/sessionExpiry';



export const AuthContext = createContext<AuthContextData>({

  signIn: async () => { },

  signOut: () => { },

  authorizationRA: '',

  authorization: '',

  authorizationForce: '',

  user: null,

});



export function AuthProvider({ children }: AuthProviderProps) {

  const navigate = useNavigate();



  const [authorization, setAuthorization] = useState('');

  const [authorizationRA, setAuthorizationRA] = useState('');

  const [authorizationForce, setAuthorizationForce] = useState('');

  const [user, setUser] = useState<LoggedInUserProps | null>(null);
  const signingOutRef = useRef(false);

  const performSignOut = useCallback((
    expired = false,
    options?: { redirect?: boolean }
  ) => {
    if (signingOutRef.current) return;
    signingOutRef.current = true;

    localStorage.clear();

    setAuthorization('');

    setAuthorizationRA('');

    setAuthorizationForce('');

    setUser(null);

    const shouldRedirect = options?.redirect ?? true;
    if (shouldRedirect) {
      navigate('/');
    }

    if (expired) void displayErrorExpired('Sessão expirada!');

    signingOutRef.current = false;

  }, [navigate]);

  const applyTokensFromStorage = useCallback(() => {
    const storedAuth = localStorage.getItem(AUTH_KEYS.AUTHORIZATION);
    const storedRA = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_RA);
    const storedForce = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_FORCE);
    setAuthorization(storedAuth || storedForce || '');
    setAuthorizationRA(storedRA || '');
    setAuthorizationForce(storedForce || '');
  }, []);

  const trySilentRefresh = useCallback(async (): Promise<boolean> => {
    if (localStorage.getItem(AUTH_KEYS.IN_FORCE) !== "true") return false;

    const result = await silentRefreshSession();
    if (!result.ok) return false;
    applyTokensFromStorage();
    signingOutRef.current = false;
    resetUnauthorizedHandledFlag();
    const userAfter = localStorage.getItem(AUTH_KEYS.SESSION);
    if (userAfter) {
      try {
        setUser(JSON.parse(userAfter));
      } catch {
        /* ignore */
      }
    }
    void displaySessionRenewed();
    return true;
  }, [applyTokensFromStorage]);

  useEffect(() => {
    setSessionUnauthorizedHandler(() => performSignOut(true));
    setRefreshBeforeUnauthorizedHandler(trySilentRefresh);
    return () => {
      setSessionUnauthorizedHandler(null);
      setRefreshBeforeUnauthorizedHandler(null);
    };
  }, [performSignOut, trySilentRefresh]);

  useSessionExpiryGuard(
    () => performSignOut(true),
    trySilentRefresh
  );

  const restoreSession = useCallback(() => {

    const isLoginPage = window.location.pathname === '/';

    if (isLoginPage) return;

    const storedAuth = localStorage.getItem(AUTH_KEYS.AUTHORIZATION);

    const storedRA = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_RA);

    const storedForce = localStorage.getItem(AUTH_KEYS.AUTHORIZATION_FORCE);

    const storedUser = localStorage.getItem(AUTH_KEYS.SESSION);

    const storedInForce = localStorage.getItem(AUTH_KEYS.IN_FORCE) === 'true';

    const hasAuth = !!(storedAuth || storedForce);

    const hasRA = storedInForce ? true : !!storedRA;

    if (!hasAuth || !hasRA || !storedUser) {
      performSignOut(true);
      return;
    }

    applyTokensFromStorage();
    setUser(JSON.parse(storedUser));

    if (isSessionExpired()) {
      void (async () => {
        const renewed = await trySilentRefresh();
        if (!renewed) {
          performSignOut(true);
          return;
        }
        const userAfter = localStorage.getItem(AUTH_KEYS.SESSION);
        if (userAfter) {
          setUser(JSON.parse(userAfter));
          applyTokensFromStorage();
        }
      })();
      return;
    }

    const remaining = getRemainingSessionMs();

    if (remaining === null) {

      try {

        const parsed = JSON.parse(storedUser) as { loginTimestamp?: number };

        const loginTs = parsed.loginTimestamp;

        const sessionMs = getDefaultSessionMs(storedInForce);

        if (typeof loginTs === 'number' && Number.isFinite(loginTs)) {

          const expiresAt = loginTs + sessionMs;

          if (Date.now() >= expiresAt) {
            performSignOut(true);
            return;
          }

          writeLogoutExpiry(expiresAt - Date.now());

        } else {

          writeLogoutExpiry(sessionMs);

        }

      } catch {
        performSignOut(true);
        return;
      }
    }

  }, [performSignOut, trySilentRefresh, applyTokensFromStorage]);


  useLayoutEffect(() => {

    restoreSession();

  }, [restoreSession]);



  const signIn = useCallback(async ({ ambient, googleToken: googleTokenProp, email, password }: SignInProps) => {

    const slug = localStorage.getItem('slug') || '';

    const inForce = localStorage.getItem('inForce') === 'true' ? true : false;

    const googleToken = googleTokenProp ?? localStorage.getItem('googleToken') ?? '';

    if (!slug || slug.trim().length === 0) {

      displayError('Slug não encontrado. Selecione o cliente novamente.');

      return;

    }

    try {

      const api = SetupNotAuth();

      const { data } = await api.post('/login-tech', {

        slug: slug.trim(),

        inForce: String(inForce),

        ambient: ambient === 'hml' ? 'hml' : 'prod',

        email: email?.trim() || '',

        password: password || '',

        googleToken: typeof googleToken === 'string' ? googleToken.trim() : '',

      });



      if (!data || data.status !== 200) {

        const errorMessage = data?.error || 'Erro ao fazer login. Tente novamente.';

        displayError(errorMessage);

        return;

      }


      if (!data.company || !data.user || !data.tokens) {

        displayError('Resposta do servidor inválida. Tente novamente.');

        if (googleToken) localStorage.removeItem('googleToken');

        return;

      }



      if (!data.tokens.Authorization && !data.tokens.AuthorizationForce) {

        displayError('Tokens de autorização não recebidos. Tente novamente.');

        return;

      }



      const clientId = inForce ? data.company.tenant_id : data.company.id;



      if (!clientId || clientId.trim().length === 0) {

        displayError('ID do cliente não encontrado na resposta do servidor.');

        return;

      }



      const userData: LoggedInUserProps = {

        clientName: data.company.name || slug,

        clientId: clientId || "",

        userName: data.user.name || "",

        ambient: data.ambient,

        userEmail: data.user.email || data.google?.email || "",

        slug,

        loginTimestamp: Date.now(),

        ambientLogged: ambient,

        inForce,

        role: data.role || "user",

        google: data.google ? {

          id: data.google.id || "",

          email: data.google.email || "",

          name: data.google.name || "",

          picture: data.google.picture || "",

        } : {

          id: "",

          email: data.user.email || "",

          name: data.user.name || "",

          picture: "",

        },

      };



      setUser(userData);

      setAuthorization(data.tokens.Authorization || '');

      setAuthorizationRA(data.tokens.AuthorizationRA || '');

      setAuthorizationForce(data.tokens.AuthorizationForce || '');



      localStorage.setItem(AUTH_KEYS.AUTHORIZATION, data.tokens.Authorization || '');

      localStorage.setItem(AUTH_KEYS.AUTHORIZATION_RA, data.tokens.AuthorizationRA || '');

      localStorage.setItem(AUTH_KEYS.AUTHORIZATION_FORCE, data.tokens.AuthorizationForce || '');

      localStorage.setItem(AUTH_KEYS.CLIENT_ID_FORCE, data.company.tenant_id || '');

      localStorage.setItem(AUTH_KEYS.SLUG, slug);

      localStorage.setItem(AUTH_KEYS.AMBIENT, ambient);

      localStorage.setItem(AUTH_KEYS.IN_FORCE, String(inForce));

      localStorage.setItem(AUTH_KEYS.SESSION, JSON.stringify(userData));

      if (data.sessionToken) {

        localStorage.setItem(AUTH_KEYS.SESSION_TOKEN, data.sessionToken);

      }



      if (googleToken) localStorage.removeItem('googleToken');



      signingOutRef.current = false;
      writeLogoutExpiry(getDefaultSessionMs(inForce));

      resetUnauthorizedHandledFlag();



      displaySuccess('Login efetuado com sucesso!');



      navigate(`/section/listForms?clientId=${clientId || ""}`);

    } catch (err: any) {

      if (typeof localStorage !== 'undefined') localStorage.removeItem('googleToken');



      let errorMessage = 'Erro ao fazer login!';



      if (err?.response) {

        const status = err.response.status;

        const errorData = err.response.data;



        if (status === 400) {

          errorMessage = errorData?.error || 'Dados inválidos. Verifique as informações e tente novamente.';

        } else if (status === 401) {

          errorMessage = errorData?.error || 'Credenciais inválidas ou token do Google expirado. Faça login novamente.';

        } else if (status === 403) {

          errorMessage = errorData?.error || 'Acesso negado. Verifique suas permissões.';

        } else if (status === 500) {

          errorMessage = errorData?.error || 'Erro no servidor. Tente novamente mais tarde.';

        } else {

          errorMessage = errorData?.error || `Erro ao fazer login (${status}). Tente novamente.`;

        }

      } else if (err?.message) {

        if (err.message.includes('Network Error') || err.message.includes('timeout')) {

          errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';

        } else {

          errorMessage = err.message;

        }

      }



      displayError(errorMessage);

      console.error('Erro ao fazer login:', err);

    }

  }, []);



  const signOut = useCallback(() => {

    performSignOut();

  }, [performSignOut]);



  return (

    <AuthContext.Provider value={{ signIn, signOut, authorizationRA, authorization, user, authorizationForce }}>

      {children}

    </AuthContext.Provider>

  );

}


